import { createContext, useContext, ReactNode, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useLogoutUser } from '@/features/auth/hooks/mutations/logout.ts';
import { clearAuthStore, useAuthStore } from '@/services/store/use-auth-store.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { useSessionQuery } from '@/features/auth/hooks/queries/session.ts';
import { TSession } from '@/features/auth/schemas/auth.schema.ts';
import { UserRolesSchema } from '@/features/users/schemas/users.schema.ts';

type SessionContextType = {
  isLoading: boolean;
  isFetching: boolean;
  isAuthenticated: boolean;
  expiresAt: number | undefined;
  logOut: () => void;
  invalidateSession: () => void;
} & TSession;

// Default value
const defaultSessionContextValue: SessionContextType = {
  userId: '',
  firstName: '',
  lastName: '',
  email: '',

  status: 'Unknown',
  role: 'Unknown',

  isLoading: true,
  isFetching: false,
  isAuthenticated: false,
  expiresAt: undefined,
  logOut: () => {},
  invalidateSession: () => {},
};

const SessionContext = createContext<SessionContextType>(defaultSessionContextValue);

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { expiresAt } = useAuthStore((state) => state.refreshToken);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const role = useAuthStore((state) => state.role) || defaultSessionContextValue.role;
  const userId = useAuthStore((state) => state.userId) || defaultSessionContextValue.userId;

  const { data, isLoading, isFetching } = useSessionQuery({ enabled: isAuthenticated });
  const logoutUser = useLogoutUser();

  const setTokenExpiration = useAuthStore((state) => state.setTokenExpiration);
  const setIsAuthenticated = useAuthStore((state) => state.setIsAuthenticated);
  const setUserRole = useAuthStore((state) => state.setUserRole);
  const setUserId = useAuthStore((state) => state.setUserId);

  const resetSession = useCallback(() => {
    setTimeout(() => {
      queryClient.resetQueries({ queryKey: ['auth', 'session'] });
    }, 600);
  }, []);

  const logOut = useCallback(() => {
    clearAuthStore();
    setTokenExpiration(Date.now());
    setIsAuthenticated(false);
    setUserRole(defaultSessionContextValue.role);
    setUserId(defaultSessionContextValue.userId);

    navigate('/');
    logoutUser.mutate();
    resetSession();
  }, []);

  useEffect(() => {
    if (data?.role && role !== data?.role) {
      try {
        const parsedRole = UserRolesSchema.parse(data.role);
        setUserRole(parsedRole);
      } catch {
        setUserRole(defaultSessionContextValue.role);
      }
    }

    if (data?.userId && userId !== data?.userId) {
      setUserId(data.userId);
    }
  }, [data?.role, data?.userId]);

  return (
    <SessionContext.Provider
      value={{
        ...defaultSessionContextValue,
        ...data,
        role,
        userId,
        isLoading,
        isFetching,
        isAuthenticated,
        expiresAt,
        logOut,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext);
